<template>
  <div class="min-h-screen bg-gradient-to-b from-slate-50 to-white">
    <!-- Q1 Launch Banner -->
    <div v-if="!bannerDismissed" class="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-slate-900 via-blue-900 to-emerald-900 text-white py-2 px-4 shadow-lg">
      <div class="container mx-auto flex items-center justify-between gap-2">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-1 sm:gap-2 text-left sm:text-center flex-1">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-white/20 backdrop-blur-sm">
              🚀 Q1 2026
            </span>
            <span class="text-xs sm:text-sm font-semibold">
              Early Adopter Launch
            </span>
          </div>
          <span class="hidden sm:inline text-xs opacity-90">|</span>
          <span class="text-xs sm:text-sm font-medium">
            🔒 Lock in $19/mo lifetime rate • Public launch March 31st
          </span>
          <router-link 
            to="/register" 
            class="hidden sm:inline-flex items-center gap-1 text-xs font-semibold hover:underline underline-offset-2 bg-white/10 px-2 py-1 rounded"
          >
            Start Trial →
          </router-link>
        </div>
        <button 
          @click="bannerDismissed = true"
          class="flex-shrink-0 p-1 hover:bg-white/10 rounded transition-colors"
          aria-label="Dismiss banner"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Navigation Header -->
    <nav :class="['fixed left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200', bannerDismissed ? 'top-0' : 'top-10']" style="transition: top 0.3s ease;">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <div class="flex items-center space-x-2">
            <img src="/InvestBuddyLogo.png" alt="InvestBuddy.ai Logo" class="w-10 h-10 rounded-full" />
            <span class="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              InvestBuddy<span class="text-green-600">.ai</span>
            </span>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center space-x-8">
            <a href="#benchmark" class="text-slate-600 hover:text-blue-600 transition-colors font-medium">Validation</a>
            <a href="#features" class="text-slate-600 hover:text-blue-600 transition-colors font-medium">Features</a>
            <a href="#premium" class="text-slate-600 hover:text-blue-600 transition-colors font-medium">Premium</a>
            <router-link to="/pricing" class="text-slate-600 hover:text-blue-600 transition-colors font-medium">Pricing</router-link>
            <router-link v-if="!isAuthenticated" to="/login" class="text-slate-600 hover:text-blue-600 transition-colors font-medium">
              Sign In
            </router-link>
            <router-link 
              v-if="!isAuthenticated" 
              to="/login" 
              class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/50 transition-all font-semibold"
            >
              Start Free Trial
            </router-link>
            <router-link 
              v-else 
              to="/dashboard" 
              class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/50 transition-all font-semibold"
            >
              Go to Dashboard
            </router-link>
          </div>

          <!-- Mobile Menu Button -->
          <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden p-2 rounded-lg hover:bg-slate-100">
            <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="md:hidden py-4 border-t border-slate-200">
          <div class="flex flex-col space-y-3">
            <a href="#benchmark" class="text-slate-600 hover:text-blue-600 transition-colors font-medium py-2" @click="mobileMenuOpen = false">Validation</a>
            <a href="#features" class="text-slate-600 hover:text-blue-600 transition-colors font-medium py-2" @click="mobileMenuOpen = false">Features</a>
            <a href="#premium" class="text-slate-600 hover:text-blue-600 transition-colors font-medium py-2" @click="mobileMenuOpen = false">Premium</a>
            <router-link to="/pricing" class="text-slate-600 hover:text-blue-600 transition-colors font-medium py-2" @click="mobileMenuOpen = false">Pricing</router-link>
            <router-link v-if="!isAuthenticated" to="/login" class="text-slate-600 hover:text-blue-600 transition-colors font-medium py-2">
              Sign In
            </router-link>
            <router-link 
              v-if="!isAuthenticated" 
              to="/login" 
              class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg text-center font-semibold"
            >
              Start Free Trial
            </router-link>
            <router-link 
              v-else 
              to="/dashboard" 
              class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg text-center font-semibold"
            >
              Go to Dashboard
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section :class="['pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden', bannerDismissed ? 'pt-32' : 'pt-44']" style="transition: padding-top 0.3s ease;">
      <!-- Animated Background with Blobs -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <!-- Blob 1 (Top-Left Purple) with Candlestick Chart Pattern -->
        <div class="absolute top-0 -left-4 w-72 h-72">
          <!-- Candlestick chart background -->
          <svg class="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <!-- Candlesticks with wicks -->
            <rect x="20" y="100" width="15" height="40" fill="#10b981" rx="2"/>
            <line x1="27.5" y1="90" x2="27.5" y2="140" stroke="#10b981" stroke-width="2"/>
            
            <rect x="45" y="80" width="15" height="50" fill="#10b981" rx="2"/>
            <line x1="52.5" y1="70" x2="52.5" y2="130" stroke="#10b981" stroke-width="2"/>
            
            <rect x="70" y="95" width="15" height="35" fill="#ef4444" rx="2"/>
            <line x1="77.5" y1="85" x2="77.5" y2="130" stroke="#ef4444" stroke-width="2"/>
            
            <rect x="95" y="70" width="15" height="45" fill="#10b981" rx="2"/>
            <line x1="102.5" y1="60" x2="102.5" y2="115" stroke="#10b981" stroke-width="2"/>
            
            <rect x="120" y="85" width="15" height="40" fill="#ef4444" rx="2"/>
            <line x1="127.5" y1="75" x2="127.5" y2="125" stroke="#ef4444" stroke-width="2"/>
            
            <rect x="145" y="65" width="15" height="50" fill="#10b981" rx="2"/>
            <line x1="152.5" y1="55" x2="152.5" y2="115" stroke="#10b981" stroke-width="2"/>
            
            <rect x="170" y="75" width="15" height="40" fill="#10b981" rx="2"/>
            <line x1="177.5" y1="65" x2="177.5" y2="115" stroke="#10b981" stroke-width="2"/>
            
            <!-- Volume bars at bottom -->
            <rect x="20" y="155" width="15" height="25" fill="#9333ea" opacity="0.4"/>
            <rect x="45" y="145" width="15" height="35" fill="#9333ea" opacity="0.4"/>
            <rect x="70" y="160" width="15" height="20" fill="#9333ea" opacity="0.4"/>
            <rect x="95" y="150" width="15" height="30" fill="#9333ea" opacity="0.4"/>
            <rect x="120" y="165" width="15" height="15" fill="#9333ea" opacity="0.4"/>
            <rect x="145" y="140" width="15" height="40" fill="#9333ea" opacity="0.4"/>
            <rect x="170" y="155" width="15" height="25" fill="#9333ea" opacity="0.4"/>
          </svg>
          <div class="absolute inset-0 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        </div>
        
        <!-- Blob 2 (Top-Right Blue) with Area Chart Pattern -->
        <div class="absolute top-0 -right-4 w-72 h-72">
          <!-- Area chart background -->
          <svg class="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:0.4" />
                <stop offset="100%" style="stop-color:#3b82f6;stop-opacity:0.05" />
              </linearGradient>
            </defs>
            <polyline 
              points="20,140 40,120 60,130 80,100 100,110 120,80 140,90 160,70 180,85" 
              fill="none" 
              stroke="#3b82f6" 
              stroke-width="3"
            />
            <polygon 
              points="20,140 40,120 60,130 80,100 100,110 120,80 140,90 160,70 180,85 180,180 20,180" 
              fill="url(#areaGradient)"
            />
            <!-- Grid lines -->
            <line x1="0" y1="60" x2="200" y2="60" stroke="#60a5fa" stroke-width="1" opacity="0.3" stroke-dasharray="5,5"/>
            <line x1="0" y1="100" x2="200" y2="100" stroke="#60a5fa" stroke-width="1" opacity="0.3" stroke-dasharray="5,5"/>
            <line x1="0" y1="140" x2="200" y2="140" stroke="#60a5fa" stroke-width="1" opacity="0.3" stroke-dasharray="5,5"/>
          </svg>
          <div class="absolute inset-0 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        </div>
        
        <!-- Blob 3 (Bottom-Left Pink) with Bar Chart Pattern -->
        <div class="absolute -bottom-8 left-20 w-72 h-72">
          <!-- Bar chart background -->
          <svg class="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <!-- Bars with growth pattern -->
            <rect x="20" y="120" width="20" height="60" fill="#ec4899" rx="2"/>
            <rect x="50" y="100" width="20" height="80" fill="#f472b6" rx="2"/>
            <rect x="80" y="90" width="20" height="90" fill="#ec4899" rx="2"/>
            <rect x="110" y="70" width="20" height="110" fill="#f472b6" rx="2"/>
            <rect x="140" y="50" width="20" height="130" fill="#ec4899" rx="2"/>
            <rect x="170" y="30" width="20" height="150" fill="#f472b6" rx="2"/>
            <!-- Trend arrow -->
            <polyline 
              points="25,130 60,110 90,95 120,75 150,55 180,35" 
              fill="none" 
              stroke="#be185d" 
              stroke-width="2"
              stroke-dasharray="4,4"
            />
            <polygon points="175,30 180,35 175,40" fill="#be185d"/>
          </svg>
          <div class="absolute inset-0 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        
        <!-- Financial chart pattern overlay (general background) -->
        <svg class="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <!-- Candlestick chart pattern -->
          <g class="chart-pattern">
            <rect x="50" y="200" width="10" height="60" fill="#3b82f6" opacity="0.6"/>
            <line x1="55" y1="180" x2="55" y2="260" stroke="#3b82f6" stroke-width="2"/>
            <rect x="100" y="150" width="10" height="80" fill="#10b981" opacity="0.6"/>
            <line x1="105" y1="130" x2="105" y2="230" stroke="#10b981" stroke-width="2"/>
            <rect x="150" y="180" width="10" height="50" fill="#ef4444" opacity="0.6"/>
            <line x1="155" y1="170" x2="155" y2="230" stroke="#ef4444" stroke-width="2"/>
            <rect x="200" y="140" width="10" height="70" fill="#10b981" opacity="0.6"/>
            <line x1="205" y1="120" x2="205" y2="210" stroke="#10b981" stroke-width="2"/>
            
            <rect x="300" y="220" width="10" height="40" fill="#3b82f6" opacity="0.6"/>
            <line x1="305" y1="210" x2="305" y2="260" stroke="#3b82f6" stroke-width="2"/>
            <rect x="350" y="160" width="10" height="90" fill="#10b981" opacity="0.6"/>
            <line x1="355" y1="140" x2="355" y2="250" stroke="#10b981" stroke-width="2"/>
            <rect x="400" y="190" width="10" height="45" fill="#ef4444" opacity="0.6"/>
            <line x1="405" y1="180" x2="405" y2="235" stroke="#ef4444" stroke-width="2"/>
            
            <rect x="500" y="170" width="10" height="55" fill="#3b82f6" opacity="0.6"/>
            <line x1="505" y1="155" x2="505" y2="225" stroke="#3b82f6" stroke-width="2"/>
            <rect x="550" y="140" width="10" height="75" fill="#10b981" opacity="0.6"/>
            <line x1="555" y1="125" x2="555" y2="215" stroke="#10b981" stroke-width="2"/>
          </g>
          
          <!-- Trend lines -->
          <polyline 
            points="50,250 150,200 250,220 350,180 450,190 550,160" 
            fill="none" 
            stroke="#6366f1" 
            stroke-width="2" 
            opacity="0.4"
            class="trend-line"
          />
          <polyline 
            points="50,280 150,260 250,240 350,220 450,210 550,190" 
            fill="none" 
            stroke="#8b5cf6" 
            stroke-width="2" 
            opacity="0.3"
            stroke-dasharray="5,5"
            class="trend-line-dashed"
          />
        </svg>
      </div>

      <div class="container mx-auto max-w-7xl relative z-10">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Hero Text -->
          <div class="space-y-4">
            <!-- Trust Badges Row -->
            <div class="flex flex-wrap gap-3 items-center">
              <!-- MCP Registry Badge -->
              <a 
                href="https://www.npmjs.com/package/investbuddy-mcp-server" 
                target="_blank"
                class="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-300 shadow-md hover:shadow-lg hover:scale-105 transition-all group"
              >
                <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-xs font-semibold text-green-900">Listed in Official MCP Registry</span>
                <span class="text-xs text-green-700 group-hover:underline">View →</span>
              </a>
              
              <!-- Validation Badge -->
              <div class="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-full border border-purple-200 shadow-md">
                <svg class="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-xs font-semibold text-purple-900">55.41% OOS Validated</span>
                <span class="text-xs text-purple-700">• 5,356 predictions</span>
              </div>
            </div>
            
            <h1 class="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              Quantitative AI Momentum Engine
              <span class="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent block mt-2">
                for Equities &amp; Portfolio Optimization
              </span>
            </h1>
            
            <p class="text-xl text-slate-600 leading-relaxed">
              Make smarter investment decisions with an institutional-grade LSTM neural network. Outperform the S&P 500 with <strong>+63.41% proven Alpha</strong>, automated risk management, and 30-day friction-optimized rebalancing.
            </p>

            <!-- Trust Signals -->
            <div class="flex flex-wrap gap-6 items-center py-4">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-sm font-bold text-slate-900">55.41% OOS Accuracy</span>
              </div>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 7H7v6h6V7z"/>
                  <path fill-rule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clip-rule="evenodd"/>
                </svg>
                <span class="text-sm font-semibold text-slate-700">Powered by Advanced LSTM AI</span>
              </div>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-sm font-semibold text-slate-700">No Credit Card Required</span>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-4">
              <router-link 
                to="/login" 
                class="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all font-semibold text-lg text-center group"
              >
                Start Free Trial
                <svg class="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </router-link>
              <a 
                href="https://chatgpt.com/g/g-694af6f8140c8191a54120429141228f-investbuddy-ai-stock-forecasts"
                target="_blank"
                rel="noopener noreferrer"
                class="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-2xl hover:shadow-green-500/50 hover:scale-105 transition-all font-semibold text-lg text-center group"
              >
                <svg class="inline-block w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.894zm16.597 3.855l-5.843-3.371 2.02-1.168a.076.076 0 0 1 .071 0l4.83 2.786a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.402-.674zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.784 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                </svg>
                Test Free on ChatGPT
              </a>
            </div>
            <!-- Track Record text link -->
            <div class="pt-1">
              <router-link to="/transparency" class="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 transition-colors group">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Read the Full 2022–2025 Walk-Forward Validation Report
                <svg class="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </router-link>
            </div>

            <!-- Trust Indicators -->
            <div class="flex items-center space-x-8 pt-4">
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm text-slate-600">No credit card required</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm text-slate-600">7-day free trial</span>
              </div>
            </div>
          </div>

          <!-- Hero Image/Mockup -->
          <div class="relative">
            <!-- Screenshot Carousel -->
            <div class="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
              <div class="relative aspect-[16/10]">
                <!-- Loading placeholder -->
                <div v-if="screenshotsLoading" class="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                  <div class="text-center space-y-4">
                    <div class="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p class="text-slate-600">Loading preview...</p>
                  </div>
                </div>

                <!-- Screenshots -->
                <TransitionGroup name="screenshot-fade">
                  <img
                    v-for="(screenshot, index) in selectedScreenshots"
                    v-show="currentScreenshotIndex === index"
                    :key="screenshot"
                    :src="`/screenshots/${screenshot}.png?v=${new Date().getTime()}`"
                    :alt="`InvestBuddy.ai Dashboard Preview ${index + 1}`"
                    class="absolute inset-0 w-full h-full object-contain bg-slate-50"
                    loading="lazy"
                    @load="onScreenshotLoad"
                  />
                </TransitionGroup>
              </div>
            </div>
            
            <!-- Screenshot Navigation Dots -->
            <div class="flex justify-center mt-6 space-x-2">
              <button
                v-for="(screenshot, index) in selectedScreenshots"
                :key="`dot-${screenshot}`"
                @click="currentScreenshotIndex = index"
                class="transition-all"
                :class="currentScreenshotIndex === index ? 'w-8 h-2 bg-blue-600 rounded-full' : 'w-2 h-2 bg-slate-300 rounded-full hover:bg-slate-400'"
                :aria-label="`Go to screenshot ${index + 1}`"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="py-20 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div class="text-center mb-16 animate-on-scroll">
          <h2 class="text-4xl font-bold text-slate-900 mb-4">Algorithmic Portfolio Management &amp; Market Regime Detection</h2>
          <p class="text-xl text-slate-600 max-w-3xl mx-auto">
            More than just analytics. Your intelligent buddy that understands your goals, tracks your progress, and guides every decision.
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Feature 1: Goal-Aligned Portfolio -->
          <div 
            class="animate-on-scroll group p-8 rounded-2xl border-2 border-slate-200 hover:border-blue-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50 overflow-hidden relative cursor-pointer"
            @click="openFeatureModal(1)"
          >
            <!-- Background decoration -->
            <div class="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -translate-y-16 translate-x-16 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            
            <div class="relative z-10">
              <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Goal-Aligned Portfolio Tracking</h3>
              <p class="text-slate-600 leading-relaxed mb-4">
                Set your financial goals and watch your portfolio behavior in real-time. Your buddy ensures every investment moves you closer to your objectives.
              </p>
              <!-- Visual indicator -->
              <div class="flex items-center space-x-2 text-sm text-blue-600 font-semibold">
                <span>Track Progress</span>
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Feature 2: AI Recommendations -->
          <div 
            class="animate-on-scroll group p-8 rounded-2xl border-2 border-slate-200 hover:border-purple-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50 overflow-hidden relative cursor-pointer" 
            style="transition-delay: 100ms;"
            @click="openFeatureModal(2)"
          >
            <div class="absolute top-0 right-0 w-32 h-32 bg-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            
            <div class="relative z-10">
              <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">AI Stock Recommendations & Discovery</h3>
              <p class="text-slate-600 leading-relaxed mb-4">
                Machine learning models analyze 46 technical indicators to discover high-potential stocks with 55.41% OOS-validated directional accuracy across 5,356 independent predictions (85 symbols, Phase 2 LSTM).
              </p>
              <div class="flex items-center space-x-2 text-sm text-purple-600 font-semibold">
                <span>Smart Analysis</span>
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Feature 3: Real-Time Insights -->
          <div 
            class="animate-on-scroll group p-8 rounded-2xl border-2 border-slate-200 hover:border-green-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50 overflow-hidden relative cursor-pointer" 
            style="transition-delay: 200ms;"
            @click="openFeatureModal(3)"
          >
            <div class="absolute top-0 right-0 w-32 h-32 bg-green-100 rounded-full -translate-y-16 translate-x-16 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            
            <div class="relative z-10">
              <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Real-Time Market Insights</h3>
              <p class="text-slate-600 leading-relaxed mb-4">
                Stay ahead with live market data, news sentiment analysis, and instant alerts when opportunities or risks emerge in your portfolio.
              </p>
              <div class="flex items-center space-x-2 text-sm text-green-600 font-semibold">
                <span>Live Updates</span>
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Feature 4: Risk Analysis -->
          <div 
            class="animate-on-scroll group p-8 rounded-2xl border-2 border-slate-200 hover:border-orange-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50 overflow-hidden relative cursor-pointer" 
            style="transition-delay: 300ms;"
            @click="openFeatureModal(4)"
          >
            <div class="absolute top-0 right-0 w-32 h-32 bg-orange-100 rounded-full -translate-y-16 translate-x-16 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            
            <div class="relative z-10">
              <div class="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Intelligent Risk Analysis</h3>
              <p class="text-slate-600 leading-relaxed mb-4">
                Comprehensive risk assessment using volatility analysis, diversification metrics, and stress testing against historical market scenarios.
              </p>
              <div class="flex items-center space-x-2 text-sm text-orange-600 font-semibold">
                <span>Risk Protection</span>
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Feature 5: Backtesting -->
          <div 
            class="animate-on-scroll group p-8 rounded-2xl border-2 border-slate-200 hover:border-pink-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50 overflow-hidden relative cursor-pointer" 
            style="transition-delay: 400ms;"
            @click="openFeatureModal(5)"
          >
            <div class="absolute top-0 right-0 w-32 h-32 bg-pink-100 rounded-full -translate-y-16 translate-x-16 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            
            <div class="relative z-10">
              <div class="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">Advanced Analytics & Backtesting</h3>
              <p class="text-slate-600 leading-relaxed mb-4">
                Test your portfolio strategy against historical events like market crashes, see projections, and optimize your allocation with data-driven insights.
              </p>
              <div class="flex items-center space-x-2 text-sm text-pink-600 font-semibold">
                <span>Test Strategies</span>
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Feature 6: AI Chat -->
          <div 
            class="animate-on-scroll group p-8 rounded-2xl border-2 border-slate-200 hover:border-indigo-500 hover:shadow-xl transition-all bg-gradient-to-br from-white to-slate-50 overflow-hidden relative cursor-pointer" 
            style="transition-delay: 500ms;"
            @click="openFeatureModal(6)"
          >
            <div class="absolute top-0 right-0 w-32 h-32 bg-indigo-100 rounded-full -translate-y-16 translate-x-16 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
            
            <div class="relative z-10">
              <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-3">AI Financial Chat Assistant</h3>
              <p class="text-slate-600 leading-relaxed mb-4">
                Ask questions, get instant answers about stocks, and receive personalized advice from your AI buddy that understands your unique portfolio.
              </p>
              <div class="flex items-center space-x-2 text-sm text-indigo-600 font-semibold">
                <span>Chat with AI</span>
                <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benchmark Validation Section -->
    <section id="benchmark" class="py-20 bg-gradient-to-br from-white via-blue-50 to-purple-50 relative overflow-hidden">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <!-- Section Header -->
        <div class="text-center mb-16 animate-on-scroll">
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-300 shadow-md mb-6">
            <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm font-semibold text-green-900">Independently Validated Performance</span>
          </div>
          
          <h2 class="text-4xl font-bold text-slate-900 mb-4">
            Out-of-Sample Validation &amp; <span class="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Walk-Forward Backtesting</span>
          </h2>
          <p class="text-xl text-slate-600 max-w-3xl mx-auto">
            True Out-of-Sample validation: 5,356 independent predictions on 85 stocks never seen during training. No cherry-picking. Full transparency.
          </p>
        </div>

        <!-- Key Metrics Grid -->
        <div class="grid md:grid-cols-4 gap-6 mb-12">
          <div class="bg-white rounded-2xl p-6 shadow-lg border-2 border-green-200 hover:shadow-xl transition-shadow">
            <div class="text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
              55.41%
            </div>
            <div class="text-slate-900 font-semibold mb-1">OOS Win Rate</div>
            <div class="text-sm text-slate-600">5,356 predictions</div>
            <div class="mt-3 pt-3 border-t border-slate-200">
              <div class="text-xs text-slate-500 font-mono">Dec 2025 – Mar 2026</div>
            </div>
          </div>

          <div class="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200 hover:shadow-xl transition-shadow">
            <div class="text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              85
            </div>
            <div class="text-slate-900 font-semibold mb-1">Symbols Tested</div>
            <div class="text-sm text-slate-600">S&P 500 + Growth</div>
            <div class="mt-3 pt-3 border-t border-slate-200">
              <div class="text-xs text-slate-500">Phase 2 universe</div>
            </div>
          </div>

          <div class="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200 hover:shadow-xl transition-shadow">
            <div class="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              +63.41%
            </div>
            <div class="text-slate-900 font-semibold mb-1">Alpha Over VOO</div>
            <div class="text-sm text-slate-600">4-year walk-forward</div>
            <div class="mt-3 pt-3 border-t border-slate-200">
              <div class="text-xs text-slate-500">+106.12% vs VOO +42.71%</div>
            </div>
          </div>

          <div class="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-200 hover:shadow-xl transition-shadow">
            <div class="text-5xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
              0.95
            </div>
            <div class="text-slate-900 font-semibold mb-1">Sharpe Ratio</div>
            <div class="text-sm text-slate-600">Institutional standard</div>
            <div class="mt-3 pt-3 border-t border-slate-200">
              <div class="text-xs text-slate-500">vs VOO 0.57</div>
            </div>
          </div>
        </div>

        <!-- Monte Carlo Teaser Card -->
        <div class="mt-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div class="flex items-center gap-3 mb-1">
              <span class="text-3xl font-extrabold text-amber-700">77.7th Percentile</span>
              <span class="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">Monte Carlo Verified</span>
            </div>
            <p class="text-slate-700 text-sm leading-relaxed">
              We tested ourselves against 1,000 random portfolios from the same stock universe.<br class="hidden sm:block">
              <span class="font-semibold">InvestBuddy beats roughly 3 out of 4 random stock pickers.</span> Full breakdown inside.
            </p>
          </div>
          <router-link
            to="/research/validation-report"
            class="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors shadow-sm whitespace-nowrap"
          >
            See The Analysis →
          </router-link>
        </div>

        <!-- Phase 3 Architecture Update Callout -->
        <div class="mt-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div class="flex items-start gap-4 flex-1">
            <div class="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow">✨</div>
            <div>
              <div class="flex items-center gap-2 mb-1">
                <span class="text-lg font-bold text-slate-900">Phase 3 Architecture Update — May 2026</span>
                <span class="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">Latest</span>
              </div>
              <p class="text-slate-700 text-sm leading-relaxed">
                Persistence Hysteresis + $5 price floor + earnings blackout gate → <strong class="text-emerald-700">+60.80% vs RSP +35.74% (+25.05pp alpha)</strong>, Sharpe 0.78 vs 0.64.
                <span class="text-slate-500 text-xs ml-1">Soft OOS — Hard OOS test: September 2026.</span>
              </p>
            </div>
          </div>
          <router-link
            to="/research/validation-report#phase3"
            class="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors shadow-sm whitespace-nowrap"
          >
            Full Report →
          </router-link>
        </div>

        <!-- Visual Validation Card -->
        <div class="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
          <div class="grid lg:grid-cols-2">
            <!-- Left: Methodology -->
            <div class="p-8 lg:p-12 bg-gradient-to-br from-slate-50 to-blue-50">
              <h3 class="text-2xl font-bold text-slate-900 mb-6">Walk-Forward Testing Methodology</h3>
              
              <div class="space-y-4 mb-8">
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-semibold text-slate-900 mb-1">18-Month Training Window</div>
                    <div class="text-sm text-slate-600">Model learns market patterns from historical data</div>
                  </div>
                </div>

                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-semibold text-slate-900 mb-1">3-Month Validation Period</div>
                    <div class="text-sm text-slate-600">Hyperparameter tuning and optimization</div>
                  </div>
                </div>

                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-semibold text-slate-900 mb-1">3-Month Out-of-Sample Testing</div>
                    <div class="text-sm text-slate-600">True predictive performance on unseen data</div>
                  </div>
                </div>
              </div>

              <div class="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-lg">
                <div class="flex items-start gap-3">
                  <svg class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                  </svg>
                  <div>
                    <div class="font-semibold text-blue-900 mb-1">Industry-Standard Validation</div>
                    <div class="text-sm text-blue-800">Walk-forward testing is used by hedge funds and institutional investors to prevent overfitting and ensure real-world applicability.</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Performance Visual -->
            <div class="p-8 lg:p-12 bg-white flex flex-col justify-center">
              <h3 class="text-2xl font-bold text-slate-900 mb-6">Performance Distribution</h3>
              
              <!-- Performance Bars -->
              <div class="space-y-6 mb-8">
                <div>
                  <div class="flex justify-between items-center mb-2">
                    <span class="text-sm font-semibold text-slate-700">Correct Predictions</span>
                    <span class="text-2xl font-bold text-green-600">55.41%</span>
                  </div>
                  <div class="w-full bg-slate-200 rounded-full h-6 overflow-hidden">
                    <div class="bg-gradient-to-r from-green-500 to-green-600 h-6 rounded-full flex items-center justify-end pr-3" style="width: 55.41%">
                      <span class="text-xs font-bold text-white">2,968 predictions</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div class="flex justify-between items-center mb-2">
                    <span class="text-sm font-semibold text-slate-700">Incorrect Predictions</span>
                    <span class="text-2xl font-bold text-red-600">44.59%</span>
                  </div>
                  <div class="w-full bg-slate-200 rounded-full h-6 overflow-visible relative">
                    <div class="bg-gradient-to-r from-red-400 to-red-500 h-6 rounded-full" style="width: 44.59%"></div>
                    <span class="absolute left-2 top-0 h-6 flex items-center text-xs font-bold text-white">2,388 predictions</span>
                  </div>
                </div>
              </div>

              <!-- Comparison Note -->
              <div class="bg-gradient-to-r from-slate-50 to-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
                <div class="flex items-center gap-3 mb-3">
                  <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-bold text-slate-900">vs. Random Chance</div>
                    <div class="text-sm text-slate-600">50% accuracy baseline</div>
                  </div>
                </div>
                <div class="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  5.41% better than random
                </div>
                <div class="text-xs text-slate-600 mt-2">p &lt; 0.0001 &nbsp;|&nbsp; 95% CI: [54.08%, 56.74%]</div>
              </div>

              <!-- Casino Analogy -->
              <div class="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-6">
                <div class="flex items-start gap-3">
                  <div class="text-2xl flex-shrink-0">🎰</div>
                  <div>
                    <div class="font-bold text-amber-900 mb-2">The Casino Analogy</div>
                    <p class="text-sm text-amber-800 leading-relaxed">
                      Retail traders think you need 80% accuracy to be profitable. The Casino house edge in Roulette is only <strong>52.6%</strong>. Across 5,356 independent 10-day predictions, our Phase 2 LSTM achieved <strong>55.41%</strong> (p &lt; 0.0001). A 55.4% edge mathematically breaks the market.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Symbol Distribution Table -->
        <div class="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mt-8">
          <div class="px-8 py-6 bg-gradient-to-r from-slate-900 to-blue-900 text-white">
            <h3 class="text-xl font-bold mb-1">Symbol-Level Transparency: How All 85 Stocks Performed</h3>
            <p class="text-sm text-slate-300">Full OOS distribution — not just the winners. Dec 2025 – Mar 2026.</p>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-slate-50 border-b border-slate-200">
                  <th class="text-left px-6 py-3 font-semibold text-slate-700">Tier</th>
                  <th class="text-center px-4 py-3 font-semibold text-slate-700">Symbols</th>
                  <th class="text-center px-4 py-3 font-semibold text-slate-700">Win Rate</th>
                  <th class="text-left px-6 py-3 font-semibold text-slate-700">Examples</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr class="hover:bg-green-50 transition-colors">
                  <td class="px-6 py-4">
                    <span class="inline-flex items-center gap-2">
                      <span class="w-3 h-3 rounded-full bg-emerald-500"></span>
                      <span class="font-semibold text-emerald-700">Excellent</span>
                    </span>
                  </td>
                  <td class="text-center px-4 py-4 font-bold text-emerald-700">10</td>
                  <td class="text-center px-4 py-4 text-slate-700">≥ 70%</td>
                  <td class="px-6 py-4 text-slate-600">XLE (79.4%), UBER (77.8%), VEA (76.2%), XOM (74.6%), VXUS (74.6%)</td>
                </tr>
                <tr class="hover:bg-blue-50 transition-colors">
                  <td class="px-6 py-4">
                    <span class="inline-flex items-center gap-2">
                      <span class="w-3 h-3 rounded-full bg-blue-500"></span>
                      <span class="font-semibold text-blue-700">Good</span>
                    </span>
                  </td>
                  <td class="text-center px-4 py-4 font-bold text-blue-700">23</td>
                  <td class="text-center px-4 py-4 text-slate-700">60 – 70%</td>
                  <td class="px-6 py-4 text-slate-600">COP (69.8%), COIN (69.8%), SHOP (69.8%), JNJ (66.7%), CVX (66.7%)</td>
                </tr>
                <tr class="hover:bg-yellow-50 transition-colors">
                  <td class="px-6 py-4">
                    <span class="inline-flex items-center gap-2">
                      <span class="w-3 h-3 rounded-full bg-yellow-500"></span>
                      <span class="font-semibold text-yellow-700">Marginal</span>
                    </span>
                  </td>
                  <td class="text-center px-4 py-4 font-bold text-yellow-700">27</td>
                  <td class="text-center px-4 py-4 text-slate-700">50 – 60%</td>
                  <td class="px-6 py-4 text-slate-600">LRCX (58.7%), JPM (58.7%), PLTR (58.7%), MSFT (57.1%), AMZN (57.1%)</td>
                </tr>
                <tr class="hover:bg-red-50 transition-colors">
                  <td class="px-6 py-4">
                    <span class="inline-flex items-center gap-2">
                      <span class="w-3 h-3 rounded-full bg-red-400"></span>
                      <span class="font-semibold text-red-700">Under 50%</span>
                    </span>
                  </td>
                  <td class="text-center px-4 py-4 font-bold text-red-700">25</td>
                  <td class="text-center px-4 py-4 text-slate-700">&lt; 50%</td>
                  <td class="px-6 py-4 text-slate-600">GS (49.2%), UNH (49.2%), MCD (49.2%), BA (19.0%), WFC (27.0%)</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="bg-slate-900 text-white">
                  <td class="px-6 py-4 font-bold">Overall</td>
                  <td class="text-center px-4 py-4 font-bold">85</td>
                  <td class="text-center px-4 py-4 font-bold text-green-400">55.41%</td>
                  <td class="px-6 py-4 text-slate-300 text-xs">5,356 predictions &nbsp;|&nbsp; p &lt; 0.0001 &nbsp;|&nbsp; 95% CI: [54.08%, 56.74%]</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="px-8 py-4 bg-slate-50 border-t border-slate-200">
            <p class="text-xs text-slate-500">The model underperforms on 25 symbols. We show you all 85 because credibility means publishing the full picture, not just the highlights.</p>
          </div>
        </div>

        <!-- CTA to Full Transparency Page -->
        <div class="text-center mt-12">
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4 flex-wrap">
            <router-link 
              to="/transparency"
              class="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all font-semibold text-lg group"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span>View Full Validation Report</span>
              <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
            
            <router-link 
              to="/benchmarks"
              class="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-2xl hover:shadow-green-500/50 transition-all font-semibold text-lg group"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              <span>VOO Benchmark Results</span>
              <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
            
            <router-link 
              to="/research/benchmarks"
              class="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-2xl hover:shadow-purple-500/50 transition-all font-semibold text-lg group"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              <span>Multi-Benchmark Analysis</span>
              <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
          <p class="text-sm text-slate-600 mt-4">
            See complete methodology, statistical tests, and institutional-grade multi-benchmark comparisons
          </p>
        </div>
      </div>
    </section>

    <!-- Advanced LSTM Engine Section -->
    <section class="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div class="text-center mb-12">
          <h2 class="text-4xl font-bold text-slate-900 mb-4">
            Phase 2 Stacked LSTM <span class="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Neural Network Architecture</span>
          </h2>
          <p class="text-xl text-slate-600 max-w-4xl mx-auto">
            Not a generic wrapper. A custom deep learning model engineered specifically for time-series financial forecasting and risk mitigation.
          </p>
        </div>

        <div class="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
          <div class="grid lg:grid-cols-2">
            <!-- Left Side: Core Architecture -->
            <div class="p-8 lg:p-12 bg-gradient-to-br from-white to-slate-50">
              <div class="space-y-6">
                <!-- Bullet Point 1 -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-bold text-slate-900 mb-2 text-lg">Model Architecture</div>
                    <div class="text-slate-600 leading-relaxed">
                      Long Short-Term Memory (LSTM) networks excel at recognizing complex, sequential patterns in historical stock data that traditional algorithms miss.
                    </div>
                  </div>
                </div>

                <!-- Bullet Point 2 -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-bold text-slate-900 mb-2 text-lg">The Data Pipeline</div>
                    <div class="text-slate-600 leading-relaxed">
                      Analyzes a dynamic 60-day lookback window with 46 technical features (OHLCV + Momentum, Volatility, Volume indicators). Phase 2 model trained on 77 symbols, validated on 85 (zero-shot generalization on 8 unseen semiconductors).
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side: Strategy Features -->
            <div class="p-8 lg:p-12 bg-gradient-to-br from-blue-50 to-indigo-50">
              <div class="space-y-6">
                <!-- Bullet Point 3 -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-bold text-slate-900 mb-2 text-lg">Friction-Optimized Rebalancing</div>
                    <div class="text-slate-600 leading-relaxed">
                      Portfolio allocations are frozen and rebalanced every 30 days. This mathematically optimized frequency captures the model's +63.41% Alpha while eliminating the lethal drag of weekly transaction costs.
                    </div>
                  </div>
                </div>

                <!-- Bullet Point 4 -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-bold text-slate-900 mb-2 text-lg">Automated Defensive Posture</div>
                    <div class="text-slate-600 leading-relaxed">
                      Features a built-in Cash Fallback mechanism. If the LSTM detects severe, systemic market weakness, it automatically recommends a 100% defensive rotation (SHV) to protect your capital.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Architecture Highlight Box -->
              <div class="mt-8 bg-white border-l-4 border-indigo-600 rounded-lg p-6 shadow-lg">
                <div class="flex items-start gap-3">
                  <svg class="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                    <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"/>
                  </svg>
                  <div>
                    <div class="font-bold text-slate-900 mb-1">2-Layer Stacked LSTM (Phase 3 Retrain #7)</div>
                    <div class="text-sm text-slate-600">47,434 trainable parameters &mdash; 67% leaner than Phase 2, engineered to eliminate overfitting</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Full Report Link -->
        <div class="text-center mt-8">
          <router-link 
            to="/research/validation-report" 
            class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-lg group transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            Read the Full 2022-2025 Walk-Forward Validation Report
            <svg class="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ChatGPT Agent Section (NEW) -->
    <section class="py-16 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden border-y-2 border-green-200">
      <!-- Animated background elements -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div class="absolute w-96 h-96 bg-green-300/20 rounded-full blur-3xl -top-48 -left-24 animate-pulse"></div>
        <div class="absolute w-96 h-96 bg-emerald-300/20 rounded-full blur-3xl -bottom-48 -right-24 animate-pulse delay-1000"></div>
      </div>

      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        <div class="text-center space-y-6">
          <!-- ChatGPT Logo + Badge -->
          <div class="flex items-center justify-center gap-3">
            <div class="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center">
              <svg class="w-10 h-10" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M37.5324 16.8707C37.9808 15.5241 38.1363 14.0974 37.9886 12.6859C37.8409 11.2744 37.3934 9.91076 36.676 8.68622C35.6126 6.83404 33.9882 5.3676 32.0373 4.4985C30.0864 3.62941 27.9098 3.40259 25.8215 3.85078C24.8796 2.7893 23.7219 1.94125 22.4257 1.36341C21.1295 0.785575 19.7249 0.491269 18.3058 0.500197C16.1708 0.495044 14.0893 1.16803 12.3614 2.42214C10.6335 3.67624 9.34853 5.44666 8.6917 7.47815C7.30085 7.76286 5.98686 8.3414 4.8377 9.17505C3.68854 10.0087 2.73073 11.0782 2.02839 12.312C0.956464 14.1591 0.498905 16.2988 0.721698 18.4228C0.944492 20.5467 1.83612 22.5449 3.268 24.1293C2.81966 25.4759 2.66413 26.9026 2.81182 28.3141C2.95951 29.7256 3.40701 31.0892 4.12437 32.3138C5.18791 34.1659 6.8123 35.6322 8.76321 36.5013C10.7141 37.3704 12.8907 37.5973 14.9789 37.1492C15.9208 38.2107 17.0786 39.0587 18.3747 39.6366C19.6709 40.2144 21.0755 40.5087 22.4946 40.4998C24.6307 40.5054 26.7133 39.8321 28.4418 38.5772C30.1704 37.3223 31.4556 35.5506 32.1119 33.5179C33.5027 33.2332 34.8167 32.6547 35.9659 31.821C37.115 30.9874 38.0728 29.9178 38.7752 28.684C39.8458 26.8371 40.3023 24.6979 40.0789 22.5748C39.8556 20.4517 38.9639 18.4544 37.5324 16.8707ZM22.4978 37.8849C20.7443 37.8874 19.0459 37.2733 17.6994 36.1501C17.7601 36.117 17.8666 36.0586 17.936 36.0161L25.9004 31.4156C26.1003 31.3019 26.2663 31.137 26.3813 30.9378C26.4964 30.7386 26.5563 30.5124 26.5549 30.2825V19.0542L29.9213 20.998C29.9389 21.0068 29.9541 21.0198 29.9656 21.0359C29.977 21.052 29.9842 21.0707 29.9867 21.0902V30.3889C29.9842 32.375 29.1946 34.2791 27.7909 35.6841C26.3872 37.0892 24.4838 37.8806 22.4978 37.8849ZM6.39227 31.0064C5.51397 29.4888 5.19742 27.7107 5.49804 25.9832C5.55718 26.0187 5.66048 26.0818 5.73461 26.1244L13.699 30.7248C13.8975 30.8408 14.1233 30.902 14.3532 30.902C14.583 30.902 14.8088 30.8408 15.0073 30.7248L24.731 25.1103V28.9979C24.7321 29.0177 24.7283 29.0376 24.7199 29.0556C24.7115 29.0736 24.6988 29.0893 24.6829 29.1012L16.6317 33.7497C14.9096 34.7416 12.8643 35.0097 10.9447 34.4954C9.02506 33.9811 7.38785 32.7263 6.39227 31.0064ZM4.29707 13.6194C5.17156 12.0998 6.55279 10.9364 8.19885 10.3327C8.19885 10.4013 8.19491 10.5228 8.19491 10.6071V19.808C8.19351 20.0378 8.25334 20.2638 8.36823 20.4629C8.48312 20.6619 8.64893 20.8267 8.84863 20.9404L18.5723 26.5542L15.206 28.4979C15.1894 28.5089 15.1703 28.5155 15.1505 28.5173C15.1307 28.5191 15.1107 28.516 15.0924 28.5082L7.04046 23.8557C5.32135 22.8601 4.06716 21.2235 3.55289 19.3046C3.03862 17.3858 3.30624 15.3413 4.29707 13.6194ZM31.955 20.0556L22.2312 14.4411L25.5976 12.4981C25.6142 12.4872 25.6333 12.4805 25.6531 12.4787C25.6729 12.4769 25.6928 12.4801 25.7111 12.4879L33.7631 17.1364C34.9967 17.849 36.0017 18.8982 36.6606 20.1613C37.3194 21.4244 37.6047 22.8486 37.4832 24.2632C37.3617 25.6779 36.8382 27.0282 35.9743 28.1596C35.1103 29.291 33.9415 30.1569 32.6047 30.6562C32.6047 30.5875 32.6086 30.4661 32.6086 30.3818V21.188C32.6102 20.9586 32.5506 20.7328 32.4358 20.5338C32.321 20.3348 32.155 20.1698 31.955 20.0556ZM35.3055 15.0128C35.2464 14.9765 35.1431 14.9142 35.069 14.8717L27.1045 10.2712C26.906 10.1554 26.6802 10.0943 26.4504 10.0943C26.2206 10.0943 25.9948 10.1554 25.7963 10.2712L16.0726 15.8858V11.9982C16.0715 11.9783 16.0753 11.9585 16.0837 11.9405C16.0921 11.9225 16.1048 11.9068 16.1207 11.8949L24.1719 7.25025C25.4053 6.53903 26.8158 6.19376 28.2383 6.25482C29.6608 6.31589 31.0364 6.78077 32.2044 7.59508C33.3723 8.40939 34.2842 9.53945 34.8334 10.8531C35.3826 12.1667 35.5464 13.6095 35.3055 15.0128ZM14.2424 21.9419L10.8752 19.9981C10.8576 19.9893 10.8423 19.9763 10.8309 19.9602C10.8195 19.9441 10.8122 19.9254 10.8098 19.9058V10.6071C10.8107 9.18295 11.2173 7.78848 11.9819 6.58696C12.7466 5.38544 13.8377 4.42659 15.1275 3.82264C16.4173 3.21869 17.8524 2.99464 19.2649 3.1767C20.6775 3.35876 22.0089 3.93941 23.1034 4.85067C23.0427 4.88379 22.937 4.94215 22.8668 4.98473L14.9024 9.58517C14.7025 9.69878 14.5366 9.86356 14.4215 10.0626C14.3065 10.2616 14.2466 10.4877 14.2479 10.7175L14.2424 21.9419ZM16.071 17.9991L20.4018 15.4978L24.7325 17.9975V22.9985L20.4018 25.4983L16.071 22.9985V17.9991Z" fill="#10A37F"/>
              </svg>
            </div>
            <span class="text-3xl font-bold text-slate-900">×</span>
            <div class="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-lg">
              <img src="/InvestBuddyLogo.png" alt="InvestBuddy.ai Logo" class="w-8 h-8 rounded-full" />
              <span class="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                InvestBuddy
              </span>
            </div>
          </div>

          <!-- Main Headline -->
          <h2 class="text-4xl md:text-5xl font-bold text-slate-900">
            Model Context Protocol (MCP) Integration
            <span class="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">for ChatGPT &amp; AI Agents</span>
          </h2>
          
          <p class="text-xl text-slate-700 max-w-2xl mx-auto">
            Get AI-powered stock predictions directly in your ChatGPT conversations. No app switching required.
          </p>

          <!-- Features Grid -->
          <div class="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto my-8">
            <div class="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-green-200">
              <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 class="font-bold text-lg text-slate-900 mb-2">Instant Predictions</h3>
              <p class="text-sm text-slate-600">Get 10-day stock forecasts in seconds without leaving ChatGPT</p>
            </div>

            <div class="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-green-200">
              <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="font-bold text-lg text-slate-900 mb-2">55.41% OOS Accuracy</h3>
              <p class="text-sm text-slate-600">Same powerful AI that powers our platform</p>
            </div>

            <div class="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-green-200">
              <div class="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
              </div>
              <h3 class="font-bold text-lg text-slate-900 mb-2">Free to Try</h3>
              <p class="text-sm text-slate-600">20 free predictions per day, no API key required</p>
            </div>
          </div>

          <!-- CTA Button -->
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <div class="flex flex-col items-center gap-3">
              <a 
                href="https://chatgpt.com/g/g-694af6f8140c8191a54120429141228f-investbuddy-ai-stock-forecasts" 
                target="_blank"
                class="group px-10 py-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl shadow-2xl hover:shadow-green-500/50 transition-all font-bold text-xl flex items-center gap-3 hover:scale-105"
              >
                <svg class="w-6 h-6" viewBox="0 0 41 41" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M37.5324 16.8707C37.9808 15.5241 38.1363 14.0974 37.9886 12.6859C37.8409 11.2744 37.3934 9.91076 36.676 8.68622C35.6126 6.83404 33.9882 5.3676 32.0373 4.4985C30.0864 3.62941 27.9098 3.40259 25.8215 3.85078C24.8796 2.7893 23.7219 1.94125 22.4257 1.36341C21.1295 0.785575 19.7249 0.491269 18.3058 0.500197C16.1708 0.495044 14.0893 1.16803 12.3614 2.42214C10.6335 3.67624 9.34853 5.44666 8.6917 7.47815C7.30085 7.76286 5.98686 8.3414 4.8377 9.17505C3.68854 10.0087 2.73073 11.0782 2.02839 12.312C0.956464 14.1591 0.498905 16.2988 0.721698 18.4228C0.944492 20.5467 1.83612 22.5449 3.268 24.1293C2.81966 25.4759 2.66413 26.9026 2.81182 28.3141C2.95951 29.7256 3.40701 31.0892 4.12437 32.3138C5.18791 34.1659 6.8123 35.6322 8.76321 36.5013C10.7141 37.3704 12.8907 37.5973 14.9789 37.1492C15.9208 38.2107 17.0786 39.0587 18.3747 39.6366C19.6709 40.2144 21.0755 40.5087 22.4946 40.4998C24.6307 40.5054 26.7133 39.8321 28.4418 38.5772C30.1704 37.3223 31.4556 35.5506 32.1119 33.5179C33.5027 33.2332 34.8167 32.6547 35.9659 31.821C37.115 30.9874 38.0728 29.9178 38.7752 28.684C39.8458 26.8371 40.3023 24.6979 40.0789 22.5748C39.8556 20.4517 38.9639 18.4544 37.5324 16.8707Z"/>
                </svg>
                Try on ChatGPT
                <svg class="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
              </a>
              
              <!-- Login Required Notice -->
              <div class="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <span class="text-xs font-semibold text-blue-800">
                  Free ChatGPT account works • Just log in first
                </span>
              </div>
            </div>

            <span class="text-sm text-slate-600">
              or <router-link to="/register" class="text-blue-600 hover:text-blue-700 font-semibold underline">get full access</router-link> with 150 predictions/day
            </span>
          </div>

          <!-- User Count Badge -->
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-green-200">
            <div class="flex -space-x-2">
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white"></div>
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 border-2 border-white"></div>
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-white"></div>
            </div>
            <span class="text-sm font-semibold text-slate-700">
              <span class="text-green-600">10+</span> investors already using our ChatGPT agent
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Premium Benefits Section -->
    <section id="premium" class="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div class="text-center mb-16 animate-on-scroll">
          <div class="inline-block px-4 py-2 bg-white/10 rounded-full mb-4 backdrop-blur-sm">
            <span class="text-sm font-semibold text-emerald-300">🚀 Q1 2026 LAUNCH SPECIAL</span>
          </div>
          <h2 class="text-4xl font-bold mb-4">Early Adopter Rate: $19/mo Lifetime Lock</h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto">
            Get Institutional-Grade Forecasts for Q1 • Lock in pricing before public launch March 31st
          </p>
        </div>

        <div class="grid lg:grid-cols-2 gap-8 items-center">
          <!-- Premium Features List -->
          <div class="space-y-6 animate-on-scroll">
            <div v-for="(feature, index) in premiumFeatures" :key="feature.title" 
                 class="flex items-start space-x-4 p-4 rounded-xl hover:bg-white/5 transition-colors"
                 :style="`transition-delay: ${index * 100}ms;`">
              <div class="flex-shrink-0">
                <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
              <div class="flex-1">
                <h4 class="text-lg font-semibold mb-1">{{ feature.title }}</h4>
                <p class="text-slate-400">{{ feature.description }}</p>
              </div>
            </div>
          </div>

          <!-- Call to Action Card -->
          <div class="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 animate-on-scroll">
            <div class="text-center space-y-6">
              <div>
                <div class="text-5xl font-bold mb-2">$0</div>
                <div class="text-slate-300">for the first 7 days</div>
                <div class="mt-4 space-y-2">
                  <div class="flex items-center justify-center gap-3">
                    <span class="text-2xl text-slate-500 line-through">$39/mo</span>
                    <span class="text-3xl font-bold text-green-400">$19/mo</span>
                  </div>
                  <div class="inline-block px-3 py-1 bg-green-500/20 rounded-full">
                    <span class="text-sm font-semibold text-green-300">🔒 Early Adopter Rate • Lifetime Lock</span>
                  </div>
                  <div class="text-xs text-slate-400">Upgrade during trial to lock in this rate forever</div>
                  <div class="text-xs text-orange-400 font-semibold">⏰ Public launch March 31st, 2026</div>
                </div>
              </div>

              <div class="space-y-3">
                <div class="flex items-center justify-center space-x-2 text-sm text-slate-300">
                  <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <span>Cancel anytime</span>
                </div>
                <div class="flex items-center justify-center space-x-2 text-sm text-slate-300">
                  <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <span>All premium features included</span>
                </div>
                <div class="flex items-center justify-center space-x-2 text-sm text-slate-300">
                  <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <span>No credit card required</span>
                </div>
              </div>

              <router-link 
                to="/register" 
                class="block w-full px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all font-semibold text-lg"
              >
                Start Your Free Trial Now
              </router-link>

              <p class="text-xs text-slate-400">
                By starting your trial, you agree to our <router-link to="/terms-of-service" class="underline hover:text-white">Terms of Service</router-link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- What's New Section -->
    <section id="whats-new" class="py-20 bg-slate-50">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div class="text-center mb-16 animate-on-scroll">
          <h2 class="text-4xl font-bold text-slate-900 mb-4">What's New at InvestBuddy.ai</h2>
          <p class="text-xl text-slate-600">Stay updated with our latest features and improvements</p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <div v-for="(update, index) in recentUpdates" :key="update.title" 
               class="animate-on-scroll bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
               :style="`transition-delay: ${index * 150}ms;`">
            <div class="flex items-center space-x-2 mb-4">
              <span class="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{{ update.tag }}</span>
              <span class="text-sm text-slate-500">{{ update.date }}</span>
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-3">{{ update.title }}</h3>
            <p class="text-slate-600 leading-relaxed mb-4">{{ update.description }}</p>
            <router-link 
              v-if="update.link" 
              :to="update.link"
              class="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-sm group"
            >
              Read Full Report
              <svg class="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Newsletter Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div class="animate-on-scroll bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h2 class="text-3xl font-bold mb-4">Stay Ahead of the Market</h2>
          <p class="text-xl mb-8 text-blue-100">
            Get weekly investment insights, market analysis, and exclusive tips delivered to your inbox.
          </p>

          <form @submit.prevent="subscribeNewsletter" class="max-w-md mx-auto">
            <div class="flex flex-col sm:flex-row gap-3">
              <input 
                v-model="newsletterEmail"
                type="email" 
                placeholder="Enter your email" 
                class="flex-1 px-6 py-4 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <button 
                type="submit"
                :disabled="isSubscribing"
                class="px-8 py-4 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-colors font-semibold whitespace-nowrap disabled:opacity-50"
              >
                {{ isSubscribing ? 'Subscribing...' : 'Subscribe' }}
              </button>
            </div>
            <p v-if="subscriptionMessage" :class="subscriptionSuccess ? 'text-green-200' : 'text-red-200'" class="mt-3 text-sm">
              {{ subscriptionMessage }}
            </p>
            <p class="text-xs text-blue-200 mt-4">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </form>
        </div>
      </div>
    </section>


    <!-- Feature Detail Modal -->
    <Transition name="modal">
      <div 
        v-if="isFeatureModalOpen && selectedFeature !== null"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="closeFeatureModal"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>
        
        <!-- Modal Container -->
        <div class="flex min-h-screen items-center justify-center p-4">
          <div 
            class="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-auto transform transition-all"
            @click.stop
          >
            <!-- Close Button -->
            <button
              @click="closeFeatureModal"
              class="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
              aria-label="Close modal"
            >
              <svg class="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <!-- Modal Content -->
            <div v-if="getFeatureById(selectedFeature)" class="p-8 md:p-12">
              <template v-for="feature in features" :key="feature.id">
                <div v-if="feature.id === selectedFeature">
                  <!-- Header -->
                  <div class="mb-8">
                    <div 
                      class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                      :class="{
                        'bg-gradient-to-br from-blue-500 to-blue-600': feature.color === 'blue',
                        'bg-gradient-to-br from-purple-500 to-purple-600': feature.color === 'purple',
                        'bg-gradient-to-br from-green-500 to-green-600': feature.color === 'green',
                        'bg-gradient-to-br from-orange-500 to-orange-600': feature.color === 'orange',
                        'bg-gradient-to-br from-pink-500 to-pink-600': feature.color === 'pink',
                        'bg-gradient-to-br from-indigo-500 to-indigo-600': feature.color === 'indigo'
                      }"
                    >
                      <svg v-if="feature.icon === 'chart-bar'" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <svg v-else-if="feature.icon === 'lightbulb'" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <svg v-else-if="feature.icon === 'lightning'" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <svg v-else-if="feature.icon === 'shield'" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <svg v-else-if="feature.icon === 'chart-line'" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                      <svg v-else-if="feature.icon === 'chat'" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{{ feature.title }}</h2>
                    <p class="text-xl text-slate-600 leading-relaxed">{{ feature.detailedDescription }}</p>
                  </div>

                  <!-- Features List -->
                  <div class="mb-8">
                    <h3 class="text-xl font-bold text-slate-900 mb-4">Key Features</h3>
                    <div class="grid md:grid-cols-2 gap-3">
                      <div 
                        v-for="(item, index) in feature.features" 
                        :key="index"
                        class="flex items-start space-x-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <svg 
                          class="w-5 h-5 mt-0.5 flex-shrink-0"
                          :class="{
                            'text-blue-600': feature.color === 'blue',
                            'text-purple-600': feature.color === 'purple',
                            'text-green-600': feature.color === 'green',
                            'text-orange-600': feature.color === 'orange',
                            'text-pink-600': feature.color === 'pink',
                            'text-indigo-600': feature.color === 'indigo'
                          }"
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        <span class="text-slate-700">{{ item }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Benefits -->
                  <div class="mb-8">
                    <h3 class="text-xl font-bold text-slate-900 mb-4">Why It Matters</h3>
                    <div class="space-y-3">
                      <div 
                        v-for="(benefit, index) in feature.benefits" 
                        :key="index"
                        class="flex items-start space-x-3"
                      >
                        <svg 
                          class="w-6 h-6 mt-0.5 flex-shrink-0"
                          :class="{
                            'text-blue-500': feature.color === 'blue',
                            'text-purple-500': feature.color === 'purple',
                            'text-green-500': feature.color === 'green',
                            'text-orange-500': feature.color === 'orange',
                            'text-pink-500': feature.color === 'pink',
                            'text-indigo-500': feature.color === 'indigo'
                          }"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span class="text-slate-700 text-lg">{{ benefit }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- CTA -->
                  <div class="pt-6 border-t border-slate-200">
                    <button
                      @click="closeFeatureModal"
                      class="w-full md:w-auto px-8 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      :class="{
                        'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white': feature.color === 'blue',
                        'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white': feature.color === 'purple',
                        'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white': feature.color === 'green',
                        'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white': feature.color === 'orange',
                        'bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white': feature.color === 'pink',
                        'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white': feature.color === 'indigo'
                      }"
                    >
                      Start Free Trial
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { apiService } from '@/services/api'

const authStore = useAuthStore()
const isAuthenticated = computed(() => authStore.isAuthenticated)

const mobileMenuOpen = ref(false)
const bannerDismissed = ref(false)
const newsletterEmail = ref('')
const isSubscribing = ref(false)
const subscriptionMessage = ref('')
const subscriptionSuccess = ref(false)

// System settings
const showTransparency = ref(false)

// Screenshot carousel state
const allScreenshots = [
  '1', '2', '3', '4', '5', '6', '7', '8',
  '9', '10', '11', '12', '13', '14', '15', '16'
]
const selectedScreenshots = ref<string[]>([])
const currentScreenshotIndex = ref(0)
const screenshotsLoading = ref(true)
const loadedScreenshotsCount = ref(0)

// Randomly select 5 screenshots on mount
const selectRandomScreenshots = () => {
  const shuffled = [...allScreenshots].sort(() => Math.random() - 0.5)
  selectedScreenshots.value = shuffled.slice(0, 5)
}

const onScreenshotLoad = () => {
  loadedScreenshotsCount.value++
  if (loadedScreenshotsCount.value >= selectedScreenshots.value.length) {
    screenshotsLoading.value = false
  }
}

// Auto-advance screenshots
let screenshotInterval: number | null = null
const startScreenshotCarousel = () => {
  screenshotInterval = window.setInterval(() => {
    currentScreenshotIndex.value = (currentScreenshotIndex.value + 1) % selectedScreenshots.value.length
  }, 4000) // Change every 4 seconds
}

const stopScreenshotCarousel = () => {
  if (screenshotInterval) {
    clearInterval(screenshotInterval)
    screenshotInterval = null
  }
}

// Feature modal state
const selectedFeature = ref<number | null>(null)
const isFeatureModalOpen = ref(false)

// Detailed feature information
const features = [
  {
    id: 1,
    title: 'Goal-Aligned Portfolio Tracking',
    color: 'blue',
    icon: 'chart-bar',
    shortDescription: 'Set your financial goals and watch your portfolio behavior in real-time. Your buddy ensures every investment moves you closer to your objectives.',
    detailedDescription: 'InvestBuddy.ai\'s goal-tracking system goes beyond simple portfolio monitoring. Set specific financial targets—whether it\'s retirement savings, a down payment, or wealth building—and our AI continuously analyzes how your current portfolio aligns with these objectives.',
    features: [
      'Set multiple financial goals with custom timeframes',
      'Real-time progress tracking with visual indicators',
      'AI-powered recommendations to stay on track',
      'Automatic rebalancing suggestions when you drift off course',
      'Goal probability analysis based on current trajectory',
      'Milestone celebrations and progress notifications'
    ],
    benefits: [
      'Stay focused on what matters most to you',
      'Make investment decisions aligned with your future',
      'Reduce emotional trading by focusing on long-term goals',
      'Get early warnings when portfolio drifts from targets'
    ],
    screenshot: null // Placeholder for future screenshot
  },
  {
    id: 2,
    title: 'AI Stock Recommendations & Discovery',
    color: 'purple',
    icon: 'lightbulb',
    shortDescription: 'Machine learning models analyze 46 technical indicators to discover high-potential stocks and provide data-driven buy/sell/hold recommendations with 55.41% OOS-validated directional accuracy across 5,356 independent predictions.',
    detailedDescription: 'Our Phase 2 LSTM achieves 55.41% out-of-sample win rate across 5,356 independent predictions (85 symbols, Dec 2025–Mar 2026, p < 0.0001, 95% CI [54.08%–56.74%]). The model outperforms the S&P 500 by +63.41% alpha over 4 years of walk-forward simulation. Every recommendation includes a confidence score and detailed reasoning. Win rate measures directional prediction accuracy on 10-day forward returns.',
    features: [
      'Phase 2 LSTM with 55.41% OOS-validated win rate',
      'Market correlation analysis (SPY/QQQ tracking)',
      '10-day prediction horizon with confidence scoring',
      'Continuously updated predictions',
      'Risk-adjusted recommendation scoring',
      'Personalized to your risk tolerance and goals',
      'Comprehensive backtesting validation'
    ],
    benefits: [
      'Discover stocks before they trend (10-day prediction window)',
      '55.41% OOS-validated directional accuracy (5,356 predictions)',
      'Data-driven decisions, not emotions',
      'Understand the "why" behind every recommendation',
      'Filter by confidence level (High/Medium/Low)',
      'Full transparency — publishes all 85 symbols including underperformers',
      'Learn from AI explanations to improve your investing'
    ],
    screenshot: null
  },
  {
    id: 3,
    title: 'Real-Time Market Insights',
    color: 'green',
    icon: 'lightning',
    shortDescription: 'Stay ahead with live market data, news sentiment analysis, and instant alerts when opportunities or risks emerge in your portfolio.',
    detailedDescription: 'Never miss a market-moving event with our real-time monitoring system. InvestBuddy.ai continuously watches your portfolio and the broader market, alerting you to important changes the moment they happen.',
    features: [
      'Live stock price updates (15-minute delay free tier)',
      'Real-time news aggregation and sentiment scoring',
      'Custom price alerts for your watchlist',
      'Portfolio event notifications (earnings, dividends, splits)',
      'Market status indicators (open/closed, pre/post market)',
      'Breaking news alerts for your holdings'
    ],
    benefits: [
      'React quickly to market-moving events',
      'Never miss important updates about your investments',
      'Stay informed without constantly checking markets',
      'Make timely decisions during volatile periods'
    ],
    screenshot: null
  },
  {
    id: 4,
    title: 'Intelligent Risk Analysis',
    color: 'orange',
    icon: 'shield',
    shortDescription: 'Comprehensive risk assessment using volatility analysis, diversification metrics, and stress testing against historical market scenarios.',
    detailedDescription: 'Understanding risk is crucial for long-term investing success. Our advanced risk analysis tools help you quantify and manage portfolio risk across multiple dimensions.',
    features: [
      'Portfolio volatility and beta calculations',
      'Sector and asset class diversification analysis',
      'Correlation analysis between holdings',
      'Value at Risk (VaR) calculations',
      'Stress testing against historical crises (2008, 2020, etc.)',
      'Risk-adjusted performance metrics (Sharpe ratio, etc.)'
    ],
    benefits: [
      'Understand your true risk exposure',
      'Identify concentration risks before they hurt you',
      'Build better-diversified portfolios',
      'Sleep better knowing your risk tolerance is matched'
    ],
    screenshot: null
  },
  {
    id: 5,
    title: 'Advanced Analytics & Backtesting',
    color: 'pink',
    icon: 'chart-line',
    shortDescription: 'Test your portfolio strategy against historical events like market crashes, see projections, and optimize your allocation with data-driven insights.',
    detailedDescription: 'Before committing capital, see how your strategy would have performed in the past. Our backtesting engine lets you test portfolio allocations against real historical data.',
    features: [
      'Backtest portfolios against historical market data',
      'Scenario analysis (market crashes, bull runs, recessions)',
      'Performance attribution analysis',
      'Monte Carlo simulations for future projections',
      'Tax-aware performance calculations',
      'Benchmark comparisons (S&P 500, sector indices)'
    ],
    benefits: [
      'Validate strategies before investing real money',
      'Learn from historical market behavior',
      'Understand worst-case scenarios',
      'Optimize allocations based on data, not guesswork'
    ],
    screenshot: null
  },
  {
    id: 6,
    title: 'AI Financial Chat Assistant',
    color: 'indigo',
    icon: 'chat',
    shortDescription: 'Ask questions, get instant answers about stocks, and receive personalized advice from your AI buddy that understands your unique portfolio.',
    detailedDescription: 'Your 24/7 investment advisor powered by advanced AI. Ask questions in plain English and get instant, contextual answers based on your portfolio and the current market.',
    features: [
      'Natural language Q&A about stocks and markets',
      'Portfolio-specific insights and analysis',
      'Stock comparisons and recommendations',
      'Market trend explanations in simple terms',
      'Learning mode with educational content',
      'Conversation history and follow-up questions'
    ],
    benefits: [
      'Get answers instantly, no waiting for advisors',
      'Learn about investing through conversation',
      'Make better decisions with AI-powered insights',
      'Available 24/7, even during off-market hours'
    ],
    screenshot: null
  }
]

const openFeatureModal = (featureId: number) => {
  selectedFeature.value = featureId
  isFeatureModalOpen.value = true
  // Prevent body scroll when modal is open
  document.body.style.overflow = 'hidden'
}

const closeFeatureModal = () => {
  isFeatureModalOpen.value = false
  selectedFeature.value = null
  // Restore body scroll
  document.body.style.overflow = 'auto'
}

const getFeatureById = (id: number) => {
  return features.find(f => f.id === id)
}

// Intersection Observer for scroll animations
const observerOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px -100px 0px'
}

let observer: IntersectionObserver | null = null

// Handle ESC key to close modal
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isFeatureModalOpen.value) {
    closeFeatureModal()
  }
}

onMounted(async () => {
  // Check if transparency page is enabled
  try {
    console.log('🔍 [HomePage] Fetching system settings...');
    const response = await apiService.getPublicSystemSettings()
    console.log('📊 [HomePage] System settings response:', response);
    if (response.success && response.data) {
      showTransparency.value = response.data.showTransparency
      console.log('✅ [HomePage] showTransparency set to:', showTransparency.value);
    }
  } catch (error) {
    console.error('❌ [HomePage] Could not load system settings:', error)
  }

  // Initialize screenshot carousel
  selectRandomScreenshots()
  startScreenshotCarousel()

  // Initialize Intersection Observer for scroll animations
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Add animation when entering viewport
        entry.target.classList.add('animate-fade-in-up')
        entry.target.classList.remove('opacity-0', 'translate-y-8')
      } else {
        // Remove animation when leaving viewport (going back up)
        entry.target.classList.remove('animate-fade-in-up')
        entry.target.classList.add('opacity-0', 'translate-y-8')
      }
    })
  }, observerOptions)

  // Observe all animatable elements
  const animatableElements = document.querySelectorAll('.animate-on-scroll')
  animatableElements.forEach((el) => observer?.observe(el))

  // Add ESC key listener for modal
  window.addEventListener('keydown', handleEscapeKey)
})

onUnmounted(() => {
  // Stop screenshot carousel
  stopScreenshotCarousel()
  
  if (observer) {
    observer.disconnect()
  }
  // Clean up modal event listener
  window.removeEventListener('keydown', handleEscapeKey)
  // Restore body scroll in case modal was open
  document.body.style.overflow = 'auto'
})

const premiumFeatures = [
  {
    title: 'Unlimited Portfolio Tracking',
    description: 'Track unlimited portfolios with real-time updates and comprehensive analytics'
  },
  {
    title: 'Advanced AI Recommendations',
    description: 'Get personalized stock recommendations powered by machine learning algorithms'
  },
  {
    title: 'Price Alerts & Notifications',
    description: 'Set custom price alerts and receive instant notifications for portfolio events'
  },
  {
    title: 'Historical Backtesting',
    description: 'Test your portfolio strategy against historical market events and crises'
  },
  {
    title: 'AI Financial Chat Assistant',
    description: 'Ask questions and get instant answers from our AI-powered financial advisor'
  },
  {
    title: 'Premium Analytics Dashboard',
    description: 'Access advanced charts, metrics, and performance projections'
  }
]

const recentUpdates = [
  {
    tag: 'PROVEN RESULTS',
    date: 'Mar 2026',
    title: '+63.41% Alpha Over VOO',
    description: 'Our Phase 2 LSTM beat VOO by +63.41% over 4 years of walk-forward simulation (+106.12% vs VOO +42.71%), with Sharpe 0.95 and 55.41% OOS directional accuracy across 5,356 independent predictions.',
    link: '/research/validation-report'
  },
  {
    tag: 'NEW FEATURE',
    date: 'Feb 2026',
    title: 'AI Stock Recommendations',
    description: 'Get data-driven stock picks powered by LSTM neural networks. Our Phase 2 model achieves 55.41% OOS win rate across 5,356 independent predictions (85 symbols, p < 0.0001).'
  },
  {
    tag: 'NEW FEATURE',
    date: 'Jan 2026',
    title: 'AI Financial Chat Assistant',
    description: 'Ask questions about stocks, get instant analysis, and receive personalized advice from our new AI chat buddy.'
  },
  {
    tag: 'IMPROVEMENT',
    date: 'Jan 2026',
    title: 'Enhanced Risk Analysis',
    description: 'New volatility metrics and stress testing against historical market scenarios for better risk assessment.'
  }
]

const subscribeNewsletter = async () => {
  if (!newsletterEmail.value) return

  isSubscribing.value = true
  subscriptionMessage.value = ''

  try {
    const response = await fetch('/api/newsletter/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: newsletterEmail.value
      })
    })

    const data = await response.json()

    if (response.ok) {
      subscriptionSuccess.value = true
      subscriptionMessage.value = data.alreadySubscribed 
        ? '✓ You are already subscribed to our newsletter!' 
        : '🎉 Successfully subscribed! Check your inbox for confirmation.'
      newsletterEmail.value = ''
      
      setTimeout(() => {
        subscriptionMessage.value = ''
      }, 5000)
    } else {
      throw new Error(data.error || 'Subscription failed')
    }
  } catch (error) {
    subscriptionSuccess.value = false
    subscriptionMessage.value = error instanceof Error 
      ? `❌ ${error.message}` 
      : '❌ Subscription failed. Please try again.'
  } finally {
    isSubscribing.value = false
  }
}
</script>

<style scoped>
.animate-on-scroll {
  opacity: 0;
  transform: translateY(2rem);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.animate-fade-in-up {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* Shimmer animation for beta badge */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 3s infinite;
}

/* Blob animation for hero background */
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

/* Financial chart pattern animations */
.chart-pattern {
  animation: chartFloat 20s ease-in-out infinite;
}

.trend-line {
  animation: trendPulse 3s ease-in-out infinite;
}

.trend-line-dashed {
  animation: trendPulse 3s ease-in-out infinite 1.5s;
}

@keyframes chartFloat {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes trendPulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
}

/* Grid background pattern */
.bg-grid-slate-100 {
  background-image: linear-gradient(to right, rgb(241 245 249 / 0.3) 1px, transparent 1px),
    linear-gradient(to bottom, rgb(241 245 249 / 0.3) 1px, transparent 1px);
  background-size: 4rem 4rem;
}

/* Modal transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div > div,
.modal-leave-active > div > div {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from > div > div,
.modal-leave-to > div > div {
  transform: scale(0.95);
  opacity: 0;
}

/* Screenshot carousel transitions */
.screenshot-fade-enter-active,
.screenshot-fade-leave-active {
  transition: opacity 0.8s ease;
}

.screenshot-fade-enter-from,
.screenshot-fade-leave-to {
  opacity: 0;
}
</style>
